import { useState, useRef } from 'react';
import { Dialog } from '@headlessui/react';
import { XMarkIcon, ArrowPathIcon, PencilIcon, CheckIcon } from '@heroicons/react/20/solid';
import { fixUniversityNames, FixUniversityNamesRequest } from '../../../api/quarantine.api';
import { useNotification } from '../../../context/NotificationContext';

// --- Types ---

interface VariantCityDto {
  name: string;
  stateName?: string;
  candidateCount: number;
}

interface VariantUrlDto {
  url: string;
  candidateCount: number;
}

interface UniversityVariantDto {
  universityIds: string[];
  name: string;
  urlCount: number;
  cities: VariantCityDto[];
  urls: VariantUrlDto[];
  isConfirmed: boolean;
  candidateCount: number;
  isAiConfirmedMatch: boolean;
  isAffiliatedCollege: boolean;
  affiliationNote: string | null;
}

export interface SimilarUniversityGroup {
  suggestedCorrectName: string;
  variants: UniversityVariantDto[];
}

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  groups: SimilarUniversityGroup[];
  totalGroups: number;
  isLoading: boolean;
  error: string | null;
  onRemoveGroup: (suggestedName: string) => void;
  token: string;
  currentPage: number;
  onPageChange: (page: number) => void;
  onRecompute: () => void;
}

function SimilarUniversityGroupsModal({ isOpen, onClose, groups, totalGroups, isLoading, error, onRemoveGroup, token, currentPage, onPageChange, onRecompute }: ModalProps) {
  const [isFixing, setIsFixing] = useState<string | null>(null);
  const [isFixingAll, setIsFixingAll] = useState(false);
  const [dismissedKeys, setDismissedKeys] = useState<Set<string>>(new Set());
  const [overriddenAffiliates, setOverriddenAffiliates] = useState<Set<string>>(new Set());
  const [confirmAction, setConfirmAction] = useState<{ message: string; onConfirm: () => void } | null>(null);
  // Drag-and-drop: track moved variants. Key = variantKey, Value = { variant, targetGroupIndex }
  const [movedVariants, setMovedVariants] = useState<Map<string, { variant: UniversityVariantDto; targetGroupIndex: number }>>(new Map());
  const [dragOverGroupIndex, setDragOverGroupIndex] = useState<number | null>(null);
  // Editable suggested names: groupIndex -> edited name
  const [editedNames, setEditedNames] = useState<Map<number, string>>(new Map());
  const [editingGroupIndex, setEditingGroupIndex] = useState<number | null>(null);
  const dragDataRef = useRef<{ variant: UniversityVariantDto; sourceGroupIndex: number } | null>(null);
  const { error: notifyError, success: notifySuccess } = useNotification();

  const getEffectiveName = (group: SimilarUniversityGroup, groupIndex: number) =>
    editedNames.get(groupIndex) ?? group.suggestedCorrectName;

  const getVariantKey = (variant: UniversityVariantDto) => variant.universityIds.join(',');

  // Get effective variants for a group (original minus moved-out plus moved-in)
  const getEffectiveVariants = (group: SimilarUniversityGroup, groupIndex: number) => {
    const originalKeys = new Set(group.variants.map(v => getVariantKey(v)));
    // Start with original variants that haven't been moved away
    const variants: UniversityVariantDto[] = group.variants.filter(v => {
      const key = getVariantKey(v);
      const moved = movedVariants.get(key);
      return !moved || moved.targetGroupIndex === groupIndex;
    });
    // Add variants moved TO this group from other groups
    movedVariants.forEach(({ variant, targetGroupIndex }) => {
      if (targetGroupIndex === groupIndex && !originalKeys.has(getVariantKey(variant))) {
        variants.push(variant);
      }
    });
    return variants;
  };

  const handleDismissVariant = (variant: UniversityVariantDto) => {
    const key = getVariantKey(variant);
    setDismissedKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const isEffectivelyAffiliated = (variant: UniversityVariantDto) =>
    variant.isAffiliatedCollege && !overriddenAffiliates.has(getVariantKey(variant));

  const getActiveVariantsFromList = (variants: UniversityVariantDto[]) =>
    variants.filter(v => !dismissedKeys.has(getVariantKey(v)) && !isEffectivelyAffiliated(v));

  const getAffiliatedVariantsFromList = (variants: UniversityVariantDto[]) =>
    variants.filter(v => !dismissedKeys.has(getVariantKey(v)) && isEffectivelyAffiliated(v));

  const getDismissedVariantsFromList = (variants: UniversityVariantDto[]) =>
    variants.filter(v => dismissedKeys.has(getVariantKey(v)));

  const handleOverrideAffiliate = (variant: UniversityVariantDto) => {
    const key = getVariantKey(variant);
    setOverriddenAffiliates(prev => {
      const next = new Set(prev);
      next.add(key);
      return next;
    });
  };

  const handleUndoOverride = (variant: UniversityVariantDto) => {
    const key = getVariantKey(variant);
    setOverriddenAffiliates(prev => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  };

  // --- Drag and Drop ---
  const handleDragStart = (variant: UniversityVariantDto, sourceGroupIndex: number) => {
    dragDataRef.current = { variant, sourceGroupIndex };
  };

  const handleDragOver = (e: React.DragEvent, groupIndex: number) => {
    e.preventDefault();
    setDragOverGroupIndex(groupIndex);
  };

  const handleDragLeave = () => {
    setDragOverGroupIndex(null);
  };

  const handleDrop = (e: React.DragEvent, targetGroupIndex: number) => {
    e.preventDefault();
    setDragOverGroupIndex(null);
    const dragData = dragDataRef.current;
    if (!dragData) return;
    const { variant, sourceGroupIndex } = dragData;
    if (sourceGroupIndex === targetGroupIndex) return;

    const key = getVariantKey(variant);
    setMovedVariants(prev => {
      const next = new Map(prev);
      next.set(key, { variant, targetGroupIndex });
      return next;
    });
    dragDataRef.current = null;
  };

  const handleUndoMove = (variant: UniversityVariantDto) => {
    const key = getVariantKey(variant);
    setMovedVariants(prev => {
      const next = new Map(prev);
      next.delete(key);
      return next;
    });
  };

  const executeFixGroup = async (group: SimilarUniversityGroup, groupIndex: number) => {
    const effectiveVariants = getEffectiveVariants(group, groupIndex);
    const variantsToFix = getActiveVariantsFromList(effectiveVariants);
    const name = getEffectiveName(group, groupIndex);
    setConfirmAction(null);
    setIsFixing(group.suggestedCorrectName);
    try {
      const payload: FixUniversityNamesRequest[] = [{
        correctName: name,
        universityIds: variantsToFix.flatMap(v => v.universityIds),
      }];
      await fixUniversityNames(payload, token);
      notifySuccess("Names Fixed", `Fixed ${variantsToFix.length} variant(s) to "${name}"`);
      onRemoveGroup(group.suggestedCorrectName);
    } catch (err: any) {
      notifyError("Fix Failed", err.message);
    } finally {
      setIsFixing(null);
    }
  };

  const handleFixGroup = (group: SimilarUniversityGroup, groupIndex: number) => {
    const effectiveVariants = getEffectiveVariants(group, groupIndex);
    const variantsToFix = getActiveVariantsFromList(effectiveVariants);
    if (variantsToFix.length === 0) return;
    const name = getEffectiveName(group, groupIndex);
    setConfirmAction({
      message: `Are you sure you want to rename ${variantsToFix.length} variant(s) to "${name}"?`,
      onConfirm: () => executeFixGroup(group, groupIndex),
    });
  };

  const fixableGroupCount = groups.filter((g, i) => getActiveVariantsFromList(getEffectiveVariants(g, i)).length > 0).length;

  const executeFixAll = async () => {
    setConfirmAction(null);
    setIsFixingAll(true);
    try {
      const payload: FixUniversityNamesRequest[] = groups.map((group, i) => ({
        correctName: getEffectiveName(group, i),
        universityIds: getActiveVariantsFromList(getEffectiveVariants(group, i)).flatMap(v => v.universityIds),
      })).filter(req => req.universityIds.length > 0);

      await fixUniversityNames(payload, token);
      notifySuccess("Names Fixed", `Fixed ${payload.length} group(s) successfully`);
      onPageChange(currentPage + 1);
    } catch (err: any) {
      notifyError("Fix Failed", err.message);
    } finally {
      setIsFixingAll(false);
    }
  };

  const handleFixAll = () => {
    if (fixableGroupCount === 0) return;
    setConfirmAction({
      message: `Are you sure you want to fix ${fixableGroupCount} group(s) on this page? This will rename university variants to their correct spelling.`,
      onConfirm: executeFixAll,
    });
  };

  // Check if a variant was moved to this group from elsewhere
  const isMovedHere = (variant: UniversityVariantDto, groupIndex: number) => {
    const key = getVariantKey(variant);
    const moved = movedVariants.get(key);
    if (!moved) return false;
    // It's "moved here" if the original group is different from target
    const originalGroupIndex = groups.findIndex(g => g.variants.some(v => getVariantKey(v) === key));
    return moved.targetGroupIndex === groupIndex && originalGroupIndex !== groupIndex;
  };

  return (
    <>
    <Dialog open={isOpen} onClose={() => { if (!confirmAction && !isFixing && !isFixingAll) onClose(); }} className="relative z-50">
      <div className="fixed inset-0 bg-black/40" aria-hidden="true" />

      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="w-full max-w-5xl overflow-hidden rounded-lg bg-white p-6 shadow-xl dark:bg-darkbglight">
          <Dialog.Title className="text-xl font-bold text-gray-900 dark:text-white flex justify-between items-center">
            Similar University Groups ({isLoading ? '...' : totalGroups} Found)
            <div className="flex items-center gap-4">
              <button
                onClick={onRecompute}
                disabled={isLoading || isFixingAll}
                title="Recompute groups"
                className="rounded-md border border-gray-300 p-1.5 text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                <ArrowPathIcon className="h-5 w-5" />
              </button>
              <button
                onClick={handleFixAll}
                disabled={isLoading || isFixingAll || fixableGroupCount === 0}
                className="rounded-md bg-entntblue px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-entntorange disabled:bg-gray-400"
              >
                {isFixingAll ? 'Fixing...' : `Fix Page ${currentPage} (${fixableGroupCount} Groups)`}
              </button>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
          </Dialog.Title>

          <div className="mt-4 max-h-[75vh] overflow-y-auto pr-4 -mr-4">
            {isLoading ? (
              <p className="text-center text-gray-500 dark:text-gray-400 py-10">Loading groups... This may take a moment.</p>
            ) : error ? (
              <p className="text-center text-red-500 py-10">Error: {error}</p>
            ) : groups.length === 0 ? (
              <p className="text-center text-gray-500 dark:text-gray-400 py-10">No similar university groups found.</p>
            ) : (
              <div className="space-y-6">
                {groups.map((group, index) => {
                  const effectiveVariants = getEffectiveVariants(group, index);
                  const variantsToFix = getActiveVariantsFromList(effectiveVariants);
                  const affiliatedColleges = getAffiliatedVariantsFromList(effectiveVariants);
                  const dismissedVariants = getDismissedVariantsFromList(effectiveVariants);
                  const isDragOver = dragOverGroupIndex === index;
                  return (
                    <div
                      key={index}
                      className={`rounded-md border p-4 transition-colors ${
                        isDragOver
                          ? 'border-entntblue border-2 bg-blue-50/50 dark:bg-blue-900/10'
                          : 'border-gray-200 bg-gray-50/50 dark:border-gray-700 dark:bg-darkbg'
                      }`}
                      onDragOver={(e) => handleDragOver(e, index)}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, index)}
                    >
                      <div className="flex justify-between items-start">
                        <h3 className="font-semibold text-lg text-gray-800 dark:text-gray-200 mb-3">
                          Group {index + 1}: <span className="text-entntblue">{group.suggestedCorrectName}</span>
                          <span className="text-sm font-normal text-gray-500 ml-2">
                            ({variantsToFix.length} variant{variantsToFix.length !== 1 ? 's' : ''}{affiliatedColleges.length > 0 ? `, ${affiliatedColleges.length} affiliated` : ''}, {effectiveVariants.reduce((sum, v) => sum + v.candidateCount, 0)} candidates)
                          </span>
                        </h3>
                        <button
                          onClick={() => handleFixGroup(group, index)}
                          disabled={isFixingAll || isFixing === group.suggestedCorrectName || variantsToFix.length === 0}
                          className="rounded bg-entntblue/10 px-2.5 py-1 text-xs font-semibold text-entntblue shadow-sm hover:bg-entntblue/20 disabled:bg-gray-200 disabled:text-gray-500"
                        >
                          {isFixing === group.suggestedCorrectName ? 'Fixing...' : `Fix ${variantsToFix.length} Name(s)`}
                        </button>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Correct Name */}
                        <div>
                          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Suggested Correct Name</p>
                          <div className="mt-1 rounded-md border border-green-200 bg-green-100 p-3 text-green-900 dark:border-green-800 dark:bg-green-900/20 dark:text-green-200 flex items-center gap-2">
                            {editingGroupIndex === index ? (
                              <>
                                <input
                                  type="text"
                                  autoFocus
                                  value={getEffectiveName(group, index)}
                                  onChange={(e) => setEditedNames(prev => { const next = new Map(prev); next.set(index, e.target.value); return next; })}
                                  onKeyDown={(e) => { if (e.key === 'Enter') setEditingGroupIndex(null); }}
                                  className="flex-1 bg-white dark:bg-darkbg border border-green-300 rounded px-2 py-1 text-sm font-bold text-green-900 dark:text-green-200 focus:outline-none focus:ring-1 focus:ring-green-400"
                                />
                                <button
                                  onClick={() => setEditingGroupIndex(null)}
                                  title="Done editing"
                                  className="p-1 rounded hover:bg-green-200 dark:hover:bg-green-800"
                                >
                                  <CheckIcon className="h-4 w-4 text-green-700 dark:text-green-300" />
                                </button>
                              </>
                            ) : (
                              <>
                                <p className="font-bold flex-1">{getEffectiveName(group, index)}</p>
                                <button
                                  onClick={() => {
                                    if (!editedNames.has(index)) {
                                      setEditedNames(prev => { const next = new Map(prev); next.set(index, group.suggestedCorrectName); return next; });
                                    }
                                    setEditingGroupIndex(index);
                                  }}
                                  title="Edit name"
                                  className="p-1 rounded hover:bg-green-200 dark:hover:bg-green-800"
                                >
                                  <PencilIcon className="h-3.5 w-3.5 text-green-700 dark:text-green-300" />
                                </button>
                              </>
                            )}
                          </div>
                        </div>

                        {/* Variants to Fix */}
                        <div>
                          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Variants to Fix ({variantsToFix.length})</p>
                          <div className="space-y-2 mt-1">
                            {variantsToFix.map(variant => {
                              const movedHere = isMovedHere(variant, index);
                              return (
                              <div
                                key={getVariantKey(variant)}
                                draggable
                                onDragStart={() => handleDragStart(variant, index)}
                                className={`rounded-md border p-3 overflow-hidden relative cursor-grab active:cursor-grabbing ${
                                  movedHere
                                    ? 'border-purple-300 bg-purple-50 text-purple-900 dark:border-purple-700 dark:bg-purple-900/20 dark:text-purple-200 ring-2 ring-purple-300'
                                    : variant.isAiConfirmedMatch
                                    ? 'border-blue-200 bg-blue-100 text-blue-900 dark:border-blue-800 dark:bg-blue-900/20 dark:text-blue-200'
                                    : 'border-red-200 bg-red-100 text-red-900 dark:border-red-800 dark:bg-red-900/20 dark:text-red-200'
                                }`}
                              >
                                <div className="absolute top-2 right-2 flex items-center gap-1">
                                  {movedHere && (
                                    <button
                                      onClick={() => handleUndoMove(variant)}
                                      title="Undo move: return to original group"
                                      className="text-[10px] px-1.5 py-0.5 rounded bg-purple-200 text-purple-800 hover:bg-purple-300"
                                    >
                                      Undo move
                                    </button>
                                  )}
                                  {overriddenAffiliates.has(getVariantKey(variant)) && (
                                    <button
                                      onClick={() => handleUndoOverride(variant)}
                                      title="Undo: move back to affiliated colleges"
                                      className="text-[10px] px-1.5 py-0.5 rounded bg-amber-200 text-amber-800 hover:bg-amber-300"
                                    >
                                      Undo
                                    </button>
                                  )}
                                  <button
                                    onClick={() => handleDismissVariant(variant)}
                                    title="Remove from this group"
                                    className={`w-5 h-5 flex items-center justify-center rounded-full text-xs font-bold leading-none ${
                                      movedHere
                                        ? 'bg-purple-300 text-purple-800 hover:bg-purple-400'
                                        : variant.isAiConfirmedMatch
                                        ? 'bg-blue-300 text-blue-800 hover:bg-blue-400'
                                        : 'bg-red-300 text-red-800 hover:bg-red-400'
                                    }`}
                                  >
                                    −
                                  </button>
                                </div>
                                <p className="font-bold pr-16">{variant.name}</p>
                                {movedHere && (
                                  <p className="text-[10px] text-purple-600 dark:text-purple-400 mt-0.5">
                                    Moved from another group
                                  </p>
                                )}
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {variant.cities.map((city, i) => (
                                    <span key={i} className={`text-xs px-1.5 py-0.5 rounded ${
                                      movedHere
                                        ? 'bg-purple-200 text-purple-800 dark:bg-purple-800 dark:text-purple-200'
                                        : variant.isAiConfirmedMatch
                                        ? 'bg-blue-200 text-blue-800 dark:bg-blue-800 dark:text-blue-200'
                                        : 'bg-red-200 text-red-800 dark:bg-red-800 dark:text-red-200'
                                    }`}>
                                      {city.name} | {city.candidateCount}
                                    </span>
                                  ))}
                                </div>
                                <p className="text-xs mt-1">{variant.urlCount} URL{variant.urlCount !== 1 ? 's' : ''} · {variant.candidateCount} candidate(s)</p>
                                {variant.urls && variant.urls.length > 0 && (
                                  <div className="mt-1.5 space-y-0.5">
                                    {variant.urls.map((u, i) => {
                                      const href = u.url.startsWith('http://') || u.url.startsWith('https://') ? u.url : `https://${u.url}`;
                                      return (
                                        <p key={i} className={`text-xs truncate max-w-[400px] ${
                                          movedHere
                                            ? 'text-purple-700 dark:text-purple-300'
                                            : variant.isAiConfirmedMatch
                                            ? 'text-blue-700 dark:text-blue-300'
                                            : 'text-red-700 dark:text-red-300'
                                        }`} title={u.url}>
                                          <a href={href} target="_blank" rel="noopener noreferrer" className="hover:underline">
                                            {u.url}
                                          </a> — {u.candidateCount}
                                        </p>
                                      );
                                    })}
                                  </div>
                                )}
                                {!variant.isAiConfirmedMatch && !movedHere && <p className="text-xs mt-1 font-semibold">AI: Likely not the same university</p>}
                              </div>
                              );
                            })}
                            {dismissedVariants.map(variant => (
                              <div key={getVariantKey(variant)} className="rounded-md border border-gray-200 bg-gray-100 p-3 text-gray-400 dark:border-gray-700 dark:bg-gray-800/30 dark:text-gray-500 overflow-hidden relative opacity-50">
                                <button
                                  onClick={() => handleDismissVariant(variant)}
                                  title="Add back to this group"
                                  className="absolute top-2 right-2 w-5 h-5 flex items-center justify-center rounded-full bg-gray-300 text-gray-600 hover:bg-gray-400 text-xs font-bold leading-none"
                                >
                                  +
                                </button>
                                <p className="font-bold pr-6 line-through">{variant.name}</p>
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {variant.cities.map((city, i) => (
                                    <span key={i} className="text-xs px-1.5 py-0.5 rounded bg-gray-200 text-gray-500">
                                      {city.name} | {city.candidateCount}
                                    </span>
                                  ))}
                                </div>
                                <p className="text-xs mt-1">{variant.urlCount} URL{variant.urlCount !== 1 ? 's' : ''} · {variant.candidateCount} candidate(s)</p>
                              </div>
                            ))}

                            {/* Drop zone hint when empty */}
                            {variantsToFix.length === 0 && dismissedVariants.length === 0 && (
                              <div className="rounded-md border-2 border-dashed border-gray-300 p-4 text-center text-sm text-gray-400 dark:border-gray-600">
                                Drop variants here
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Affiliated Colleges Section */}
                      {affiliatedColleges.length > 0 && (
                        <div className="mt-4 border-t border-gray-200 dark:border-gray-700 pt-3">
                          <p className="text-sm font-medium text-amber-700 dark:text-amber-400 mb-2">
                            Affiliated Colleges ({affiliatedColleges.length}) — these won't be renamed
                          </p>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            {affiliatedColleges.map(variant => (
                              <div
                                key={getVariantKey(variant)}
                                draggable
                                onDragStart={() => handleDragStart(variant, index)}
                                className="rounded-md border border-amber-200 bg-amber-50 p-3 overflow-hidden relative text-amber-900 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-200 cursor-grab active:cursor-grabbing"
                              >
                                <div className="absolute top-2 right-2 flex items-center gap-1">
                                  <button
                                    onClick={() => handleOverrideAffiliate(variant)}
                                    title="Add to variants to fix"
                                    className="w-5 h-5 flex items-center justify-center rounded-full bg-green-300 text-green-800 hover:bg-green-400 text-xs font-bold leading-none"
                                  >
                                    +
                                  </button>
                                  <button
                                    onClick={() => handleDismissVariant(variant)}
                                    title="Dismiss"
                                    className="w-5 h-5 flex items-center justify-center rounded-full bg-amber-300 text-amber-800 hover:bg-amber-400 text-xs font-bold leading-none"
                                  >
                                    −
                                  </button>
                                </div>
                                <p className="font-bold pr-28">{variant.name}</p>
                                {variant.affiliationNote && (
                                  <p className="text-xs mt-0.5 italic text-amber-700 dark:text-amber-400">{variant.affiliationNote}</p>
                                )}
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {variant.cities.map((city, i) => (
                                    <span key={i} className="text-xs px-1.5 py-0.5 rounded bg-amber-200 text-amber-800 dark:bg-amber-800 dark:text-amber-200">
                                      {city.name} | {city.candidateCount}
                                    </span>
                                  ))}
                                </div>
                                <p className="text-xs mt-1">{variant.urlCount} URL{variant.urlCount !== 1 ? 's' : ''} · {variant.candidateCount} candidate(s)</p>
                                {variant.urls && variant.urls.length > 0 && (
                                  <div className="mt-1.5 space-y-0.5">
                                    {variant.urls.map((u, i) => {
                                      const href = u.url.startsWith('http://') || u.url.startsWith('https://') ? u.url : `https://${u.url}`;
                                      return (
                                        <p key={i} className="text-xs truncate max-w-[400px] text-amber-700 dark:text-amber-300" title={u.url}>
                                          <a href={href} target="_blank" rel="noopener noreferrer" className="hover:underline">
                                            {u.url}
                                          </a> — {u.candidateCount}
                                        </p>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Pagination */}
          {!isLoading && !error && totalGroups > 0 && (
            <div className="mt-4 flex items-center justify-between border-t border-gray-200 pt-3 dark:border-gray-700">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Showing {(currentPage - 1) * 10 + 1}–{Math.min(currentPage * 10, totalGroups)} of {totalGroups} groups
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => onPageChange(currentPage - 1)}
                  disabled={currentPage <= 1 || isLoading || isFixingAll}
                  className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  Page {currentPage} of {Math.ceil(totalGroups / 10)}
                </span>
                <button
                  onClick={() => onPageChange(currentPage + 1)}
                  disabled={currentPage >= Math.ceil(totalGroups / 10) || isLoading || isFixingAll}
                  className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </Dialog.Panel>
      </div>
    </Dialog>

    {/* Confirmation Dialog */}
    {confirmAction && (
      <Dialog open={true} onClose={() => setConfirmAction(null)} className="relative z-[60]">
        <div className="fixed inset-0 bg-black/50" aria-hidden="true" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl dark:bg-darkbglight">
            <Dialog.Title className="text-lg font-semibold text-gray-900 dark:text-white">
              Confirm Action
            </Dialog.Title>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
              {confirmAction.message}
            </p>
            <div className="mt-4 flex justify-end gap-3">
              <button
                onClick={() => setConfirmAction(null)}
                className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  confirmAction.onConfirm();
                }}
                className="rounded-md bg-entntblue px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-entntorange"
              >
                Confirm
              </button>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>
    )}
    </>
  );
}

export default SimilarUniversityGroupsModal;
